import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Box } from '@mui/material';

import { SCUIContent } from '@/atomic/organisms/SCUIContent/SCUIContent';

import {
    dispatchPaylensInboundMessageEvent,
    getOriginFromIframeSrc,
    PAYLENS_READY_MESSAGE_TYPE,
    PAYLENS_REFRESH_TOKEN_MESSAGE_TYPE,
    postPaylensTokenToIframe,
} from '@/utils/paylensEmbedBridge';

import { PL } from './paylens.classes';

const PAYLENS_EMBED_TEST_URL = 'https://paylens-preprod.lcwaikiki.com/paylens/service/bo-embed.html';
const PAYLENS_EMBED_PROD_URL = 'https://checkout.hq.lcwecomtr.com/paylens/service/bo-embed.html';

const getPaylensEmbedUrl = (): string => {
    const envName = (import.meta.env.VITE_ENVIRONMENT || '').toString().toLowerCase();

    if (envName === 'preprod') {
        return PAYLENS_EMBED_TEST_URL;
    }

    if (envName === 'prod' || envName === 'master') {
        return PAYLENS_EMBED_PROD_URL;
    }

    return PAYLENS_EMBED_TEST_URL;
};

export const Paylens = () => {
    const iframeSrc = useMemo(() => getPaylensEmbedUrl(), []);

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const iframeOrigin = useMemo(() => getOriginFromIframeSrc(iframeSrc), [iframeSrc]);

    const sendTokenToIframe = useCallback(
        (reason: 'embed-load' | 'embed-ready' | 'embed-refresh', forceRefresh = false) => {
            const contentWindow = iframeRef.current?.contentWindow;

            if (!iframeOrigin || !contentWindow) {
                return;
            }

            void postPaylensTokenToIframe(contentWindow, iframeOrigin, { reason, forceRefresh });
        },

        [iframeOrigin]
    );

    useEffect(() => {
        if (!iframeOrigin) {
            return;
        }

        const onMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }

            if (event.origin !== iframeOrigin) {
                return;
            }

            const messageType = event.data?.type;

            if (messageType === PAYLENS_READY_MESSAGE_TYPE) {
                dispatchPaylensInboundMessageEvent(messageType, iframeOrigin);

                sendTokenToIframe('embed-ready');

                return;
            }

            if (messageType === PAYLENS_REFRESH_TOKEN_MESSAGE_TYPE) {
                dispatchPaylensInboundMessageEvent(messageType, iframeOrigin);

                sendTokenToIframe('embed-refresh', true);
            }
        };

        window.addEventListener('message', onMessage);

        return () => window.removeEventListener('message', onMessage);
    }, [iframeOrigin, sendTokenToIframe]);

    return (
        <SCUIContent className={PL.page}>
            <Box
                className={PL.frameCard}

                sx={{
                    width: '100%',

                    height: '100%',

                    minHeight: 'calc(100vh - 160px)',

                    borderRadius: 2,

                    overflow: 'hidden',

                    border: '1px solid',

                    borderColor: 'divider',

                    bgcolor: 'background.paper',
                }}
            >
                <Box
                    ref={iframeRef}

                    className={PL.iframe}

                    component="iframe"

                    title="Paylens"

                    src={iframeSrc}

                    onLoad={() => sendTokenToIframe('embed-load')}

                    sx={{ width: '100%', height: '725px', border: 0 }}
                />
            </Box>
        </SCUIContent>
    );
};
